import { useState, useMemo } from 'react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  AlertTitle,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Separator,
  Spinner,
} from '@monksflow/monks-ui';
import { AlertCircleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useWorkspaceScheduleOverride } from '@/features/admin/model/useWorkspaceScheduleOverride';
import { notificationService } from '@/lib/services/notification.service';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import type { SchemaWidget } from '@/shared/ui/SchemaForm';
import { ClearWorkspaceOpportunitiesAction } from './ClearWorkspaceOpportunitiesAction';
import { GenerationEndDateField } from './GenerationEndDateField';
import { ScheduleFields } from './ScheduleFields';
import { ToolConfigForm } from './ToolConfigForm';
import { hasRequiredKnowledgeBase } from './toolConfigValidation';

const DEEP_RESEARCH_TOOL_ID = 'deep-research-opportunity-identifier';

interface WorkspaceConfigRowProps {
  config: {
    id: string;
    workspaceId?: string;
    data: Record<string, unknown>;
  };
  workspaceName: string;
  workspaceLogoUrl: string | null;
  toolId: string;
  jsonSchema: Record<string, unknown>;
  onSave: (data: Record<string, unknown>) => void | Promise<void>;
  onRemove: () => void | Promise<void>;
  isSaving: boolean;
  isRemoving: boolean;
  /** Lets the list close this row once its save lands; the list owns which row is open. */
  onSaved: () => void;
  isPartial?: boolean;
  uiSchemaOverride?: Record<string, unknown>;
  /** Control for `kbSelect`. Injected by the page (dependency injection). */
  kbSelectWidget?: SchemaWidget;
  /** Control for `kbMultiSelect`. Injected by the page (dependency injection). */
  kbMultiSelectWidget?: SchemaWidget;
}

export function WorkspaceConfigRow({
  config,
  workspaceName,
  workspaceLogoUrl,
  toolId,
  jsonSchema,
  onSave,
  onRemove,
  isSaving,
  isRemoving,
  onSaved,
  isPartial,
  uiSchemaOverride,
  kbSelectWidget,
  kbMultiSelectWidget,
}: WorkspaceConfigRowProps) {
  const { t } = useTranslation('admin');

  const [formData, setFormData] = useState<Record<string, unknown>>(config.data);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const isScheduleOverrideEnabled = useBooleanFlagValue(
    'workspace_opportunity_schedule_override',
    false,
  );
  const isGenerationCutoffEnabled = useBooleanFlagValue(
    'workspace_opportunity_generation_cutoff',
    false,
  );

  const originalData = useMemo(() => JSON.stringify(config.data), [config.data]);
  const isConfigDirty = JSON.stringify(formData) !== originalData;
  const hasKnowledgeBase = useMemo(
    () => hasRequiredKnowledgeBase(jsonSchema, formData),
    [jsonSchema, formData],
  );
  // The badges report what is stored, not what is being typed: a row that changes its flags while
  // you edit is claiming something the server has not agreed to yet.
  const savedHasKnowledgeBase = useMemo(
    () => hasRequiredKnowledgeBase(jsonSchema, config.data),
    [jsonSchema, config.data],
  );

  const schedule = useWorkspaceScheduleOverride(config.workspaceId ?? '');

  // The section always shows for this tool: a workspace that cannot be scheduled needs to be told
  // why, and silently dropping the section reads as a bug. Two different reasons land here — no
  // knowledge base, and a configuration never saved — and each names the next action.
  const showSchedule =
    toolId === DEEP_RESEARCH_TOOL_ID && !!config.workspaceId && isScheduleOverrideEnabled;
  const canEditSchedule = hasKnowledgeBase && schedule.exists;

  // A configuration with no knowledge base behind it cannot produce anything, and predates the
  // rule that now blocks the combination. Flagged on the row so it is findable without opening
  // each workspace in turn. Deliberately not tied to having a schedule: a workspace that never
  // had a knowledge base never got a job row either, so those are exactly the ones a
  // schedule-based test would miss.
  const needsKnowledgeBase = toolId === DEEP_RESEARCH_TOOL_ID && !savedHasKnowledgeBase;

  // One button covers the configuration and the schedule. Each half is written only when it
  // changed; saving an untouched inherited schedule would create an override nobody asked for.
  const handleSave = async () => {
    if (isConfigDirty || isPartial) {
      await onSave(formData);
    }

    if (canEditSchedule && schedule.isDirty) {
      try {
        await schedule.save();
        notificationService.showSuccess(
          schedule.hasOverride
            ? t('admin.workspaceScheduleSection.workspaceScheduleUpdated')
            : t('admin.workspaceScheduleSection.workspaceOverrideCreated'),
          { description: t('admin.workspaceScheduleSection.changesWillTakeEffectFrom') },
        );
      } catch {
        notificationService.showError(
          t('admin.workspaceScheduleSection.failedToUpdateWorkspaceSchedule'),
          { description: t('admin.workspaceScheduleSection.pleaseTryAgain') },
        );
        return;
      }
    }

    onSaved();
  };

  const handleResetSchedule = async () => {
    try {
      await schedule.reset();
      notificationService.showSuccess(t('admin.workspaceScheduleSection.overrideRemoved'), {
        description: t('admin.workspaceScheduleSection.thisWorkspaceNowFollowsThe'),
      });
    } catch {
      notificationService.showError(t('admin.workspaceScheduleSection.failedToRemoveOverride'), {
        description: t('admin.workspaceScheduleSection.pleaseTryAgain'),
      });
    }
  };

  const handleDelete = async () => {
    await onRemove();
    setShowDeleteConfirm(false);
  };

  const hasChanges = isConfigDirty || isPartial || (canEditSchedule && schedule.isDirty);

  return (
    <AccordionItem
      value={config.workspaceId ?? config.id}
      // Each item is a card here rather than a row in a divided list, so the component's
      // `last:border-b-0` — right for dividers — would leave the last card open at the bottom.
      className="overflow-hidden rounded-lg border last:border-b bg-card"
    >
      <AccordionTrigger className="px-md hover:no-underline">
        <div className="flex flex-1 items-center gap-md">
          <Avatar className="size-8 rounded-md">
            <AvatarImage src={workspaceLogoUrl ?? undefined} alt={workspaceName} />
            <AvatarFallback>{workspaceName.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>

          <span className="flex-1 truncate text-left text-body-lg font-medium">
            {workspaceName}
          </span>

          {needsKnowledgeBase && (
            <Badge variant="warning" roundness="full">
              {t('admin.workspaceConfigRow.needsKnowledgeBase')}
            </Badge>
          )}

          {showSchedule && schedule.hasOverride && (
            <Badge variant="secondary" roundness="full">
              {t('admin.workspaceConfigRow.customSchedule')}
            </Badge>
          )}

          {/* Last, always: it is the one badge every scheduled workspace carries, so a fixed
              position is what lets the column be read down the list rather than hunted for. */}
          {showSchedule && schedule.exists && (
            <Badge variant={schedule.saved.enabled ? 'positive' : 'secondary'} roundness="full">
              {schedule.saved.enabled
                ? t('admin.scheduleEditor.enabled')
                : t('admin.scheduleEditor.disabled')}
            </Badge>
          )}
        </div>
      </AccordionTrigger>

      <AccordionContent>
        <Separator />

        <div className="flex flex-col gap-xl bg-muted/30 p-xl">
          <section className="flex flex-col gap-sm">
            <h3 className="text-label-md font-semibold">
              {t('admin.workspaceConfigRow.configuration')}
            </h3>
            <ToolConfigForm
              toolId={toolId}
              jsonSchema={jsonSchema}
              formData={formData}
              onChange={setFormData}
              disabled={isSaving}
              uiSchemaOverride={uiSchemaOverride}
              kbSelectWidget={kbSelectWidget}
              kbMultiSelectWidget={kbMultiSelectWidget}
            />
          </section>

          {showSchedule && (
            <>
              <Separator />
              <section className="flex flex-col gap-sm">
                <div className="flex items-center justify-between gap-sm">
                  <div className="flex items-center gap-xs">
                    <h3 className="text-label-md font-semibold">
                      {t('admin.workspaceScheduleSection.runSchedule')}
                    </h3>
                    {schedule.exists && (
                      <span className="text-body-sm text-muted-foreground">
                        {schedule.hasOverride
                          ? t('admin.workspaceScheduleSection.override')
                          : t('admin.workspaceScheduleSection.inheritingOrganization')}
                      </span>
                    )}
                  </div>
                  {schedule.hasOverride && (
                    <div className="flex flex-col items-end gap-2xs">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void handleResetSchedule()}
                        disabled={schedule.isResetting}
                      >
                        {schedule.isResetting
                          ? t('admin.workspaceScheduleSection.resetting')
                          : t('admin.workspaceScheduleSection.resetToOrganization')}
                      </Button>
                      {schedule.generationEndDate !== null && (
                        <p className="text-body-sm text-muted-foreground">
                          {t('admin.workspaceScheduleSection.resetAlsoClearsCutoff')}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {schedule.isLoading ? (
                  <div className="flex justify-center py-lg">
                    <Spinner />
                  </div>
                ) : (
                  <>
                    {!hasKnowledgeBase && (
                      <Alert icon={<AlertCircleIcon />}>
                        <AlertTitle>
                          {t('admin.workspaceConfigRow.scheduleUnavailableNoKnowledgeBase')}
                        </AlertTitle>
                        <AlertDescription>
                          {t('admin.workspaceConfigRow.selectAKnowledgeBaseAbove')}
                        </AlertDescription>
                      </Alert>
                    )}

                    {hasKnowledgeBase && !schedule.exists && (
                      <Alert icon={<AlertCircleIcon />}>
                        <AlertTitle>
                          {t('admin.workspaceConfigRow.scheduleNotCreatedYet')}
                        </AlertTitle>
                        <AlertDescription>
                          {t('admin.workspaceConfigRow.saveTheConfigurationToCreate')}
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Shown even when it cannot be edited: the reason sits right above it, and a
                        greyed-out control says "not yet" where an absent one says "never". */}
                    <ScheduleFields
                      value={schedule.value}
                      onChange={schedule.setValue}
                      disabled={isSaving || schedule.isSaving || !canEditSchedule}
                    />

                    {isGenerationCutoffEnabled && (
                      <GenerationEndDateField
                        value={schedule.generationEndDate}
                        onChange={schedule.setGenerationEndDate}
                        disabled={isSaving || schedule.isSaving || !canEditSchedule}
                      />
                    )}
                  </>
                )}
              </section>
            </>
          )}

          {toolId === DEEP_RESEARCH_TOOL_ID && config.workspaceId && (
            <>
              <Separator />
              <ClearWorkspaceOpportunitiesAction
                workspaceId={config.workspaceId}
                workspaceName={workspaceName}
              />
            </>
          )}

          <Separator />

          <div className="flex items-center justify-between gap-md">
            <p className="text-body-sm text-muted-foreground">
              {!hasKnowledgeBase && t('admin.workspaceConfigRow.selectAKnowledgeBaseBefore')}
            </p>
            <div className="flex gap-xs">
              <Button
                variant="destructive"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSaving}
              >
                {t('admin.workspaceConfigRow.delete')}
              </Button>
              <Button
                onClick={() => void handleSave()}
                disabled={!hasKnowledgeBase || !hasChanges || isSaving || schedule.isSaving}
              >
                {t('admin.workspaceConfigRow.save')}
              </Button>
            </div>
          </div>

          <DeleteConfirmationModal
            open={showDeleteConfirm}
            isDeleting={isRemoving}
            onCancel={() => setShowDeleteConfirm(false)}
            onConfirmDelete={() => void handleDelete()}
            resourceName={workspaceName}
            warningMessage={t('admin.workspaceConfigRow.thisWillRemoveTheWorkspace')}
          />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
